import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Card,
  CardBody,
  CardFooter,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from 'reactstrap';

import {
  Button,
  useFeatures,
  useSettings,
  useSession,
  Form,
} from '@cordata/buffet';

import useCit from '~/utils/cit/hook';
import MedicalEventCitFields from '~/components/MedicalEvent/Form/CIT';
import PublicFields from './PublicFields';
import PatientLookup from './PatientLookup';

const CitForm = ({ data, disabled }) => {
  const history = useHistory();
  const { isAuthed } = useSession();
  const { requiresSupervisoryApproval } = useSettings();
  const { hasSupervisoryApproval } = useFeatures();
  const [modalAttrs, setModalAttrs] = useState({
    isOpen: false,
    msg: '',
    action: () => {},
  });

  const {
    authStatus,
    formData,
    formDataStatus,
    validationSchema,
    initialValues,
    initialStatus,
    previousData,
    actions: {
      mapDataToFormData,
      submit,
      approveSubmission,
      rejectSubmission,
      setAuthStatus,
      setPreviousData,
    },
  } = useCit();

  const handleSubmit = (v, {
    setErrors,
    setSubmitting,
    resetForm,
  }) =>
    submit(v)
      .then((r) => {
        if (requiresSupervisoryApproval?.settingValue === 'true' && hasSupervisoryApproval && !v.approvalLocked) {
          if (v.approvalStatus === 'approved') {
            return approveSubmission(v);
          }

          if (v.approvalStatus === 'rejected') {
            return rejectSubmission(v);
          }
        }

        return r;
      })
      .then((r) => {
        const rdata = r?.data?.data;

        toast.success('Form successfully saved!');

        if (rdata?.secureHref) {
          window.open(rdata.secureHref);
        }

        if (rdata && 'emailSent' in rdata && rdata.emailSent) {
          const facilityNames = Array.isArray(v.facilityUid) && v.facilityUid.length > 0
            ? v.facilityUid.map(f => f.facilityName).join(', ')
            : v.facilityUid?.facilityName;
          const facilityText = Array.isArray(v.facilityUid) && v.facilityUid.length > 1 ? 'facilities' : 'facility';
          toast.success(`Success! An email has been sent to the ${facilityNames} ${facilityText} referring this client for followup.`);

          if (!isAuthed) {
            resetForm({ success: true });
            setAuthStatus('INVALID');
          } else {
            history.push('/home');
          }
        } else if (rdata && 'emailSent' in rdata && !rdata.emailSent) {
          const action = () => {
            if (isAuthed) {
              history.push('/home');
            } else {
              resetForm({ success: true });
              setAuthStatus('INVALID');
              setModalAttrs({
                isOpen: false,
                action: () => {},
              });
            }
          };

          const facilityNames = Array.isArray(v.facilityUid) && v.facilityUid.length > 0
            ? v.facilityUid.map(f => f.facilityName).join(', ')
            : v.facilityUid?.facilityName;
          const facilityText = Array.isArray(v.facilityUid) && v.facilityUid.length > 1 ? 'facilities' : 'facility';

          if (facilityNames) {
            setModalAttrs({
              isOpen: true,
              msg: `Whoops - an email could not be sent to the ${facilityNames} ${facilityText}. Please contact your CIT.`,
              action,
            });
          } else if (!isAuthed) {
            resetForm({ success: true });
            setAuthStatus('INVALID');
          } else if (isAuthed) {
            history.push('/home');
          }
        } else if (!isAuthed) {
          resetForm({ success: true });
          setAuthStatus('INVALID');
        } else if (isAuthed) {
          history.push('/home');
        }

        return r;
      })
      .catch((e) => {
        const errs = e.response ? e.response.data.errors : e;
        const serverError = e.response?.data.message?.split('\n')[1];

        if (serverError) {
          console.error('CIT SUBMISSION ERROR:', serverError);
          toast.error(serverError);
        }

        setErrors({
          ...errs,
          server: e,
          errs,
        });

        if (errs && Object.keys(errs).some(er => Object.keys(v).includes(er))) {
          Object.keys(errs).forEach(er => setErrors({ [er]: errs[er] !== 'Required' ? errs[er] : 'is required' }));
        }

        return e;
      })
      .finally(() => {
        setSubmitting(false);
      });

  useEffect(() => {
    if (data && formDataStatus === 'SUCCESS') {
      const mappedData = mapDataToFormData(data, formData);
      setPreviousData({
        ...data,
        ...mappedData,
        approvalLocked: data.approvalStatus !== 'pending',
      });
    }
  }, [formDataStatus, data]);

  return (
    <Card>
      <Form
        displayName="CrisisInterventionTeam"
        enableReinitialize

        validateOnBlur
        validateOnChange={false}
        validationSchema={validationSchema}

        initialValues={previousData ? { ...initialValues, ...previousData } : initialValues}
        initialStatus={initialStatus}

        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
        }) => (
          <>
            <CardBody className="p-0">

              {(isAuthed && previousData) && (
                <MedicalEventCitFields
                  formData={formData}
                  disabled={disabled}
                />
              )}

              {!isAuthed && (
                <PublicFields />
              )}

              <PatientLookup />
            </CardBody>

            {(authStatus === 'VALID' || isAuthed) && (
              <CardFooter className="d-flex">
                <div className="ml-auto">
                  { isAuthed && (
                    <Button
                      type="button"
                      icon="times"
                      outline
                      color="tertiary"
                      onClick={history.goBack}
                    >
                      Cancel
                    </Button>
                  )}

                  { !disabled && (
                    <Button
                      type="submit"
                      disabled={(authStatus !== 'VALID' && !isAuthed) || isSubmitting}
                    >
                      {isSubmitting ? 'Working ...' : 'Save'}
                    </Button>
                  )}
                </div>
              </CardFooter>
            )}

            <Modal
              isOpen={modalAttrs.isOpen}
              toggle={modalAttrs.action}
            >
              <ModalHeader>
                Failed to send email
              </ModalHeader>

              <ModalBody>
                {modalAttrs.msg}
              </ModalBody>

              <ModalFooter>
                <div className="ml-auto">
                  <Button outline onClick={modalAttrs.action}>
                    Okay
                  </Button>
                </div>
              </ModalFooter>
            </Modal>
          </>
        )}
      </Form>
    </Card>
  );
};

CitForm.propTypes = {
  data: PropTypes.shape({
    approvalStatus: PropTypes.string,
  }),
  disabled: PropTypes.bool,
};

CitForm.defaultProps = {
  data: undefined,
  disabled: false,
};

export default CitForm;
